/**
 * AppMobile.tsx
 *
 * Mobile-optimized version of the Transit Route Planner.
 * Key differences from App.tsx:
 * - Uses LayoutMobile for stacked vertical layout
 * - Date/time inputs are side-by-side
 * - Uses AppMobile.css for mobile-specific styles
 */

import React, { useEffect, useCallback, useMemo, useState } from "react";
import { LoadScript } from "@react-google-maps/api";
import InputGroupMobile from "./InputGroupMobile";
import MapContainer from "./MapContainer";
import RouteCard from "./RouteCard";
import WarningMessage from "./WarningMessage";
import FindRoutesButton from "./FindRoutesButton";
import LayoutMobile from "./LayoutMobile";
import { filterLatLong, groupIdenticalRoutes } from "./transitUtils";
import {
  fetchDirections,
  getDirectionsErrorMessage,
} from "./googleMapsService";
import useDynamicDateTime from "./hooks/dynamicdatetime";
import { transitPlannerReducer, initialState } from "./TransitPlannerReducer";
import { getConfigSettings } from "./configUtils";
import { debounce } from "lodash";
import { LicenseProvider, useLicense } from "./contexts/LicenseContext";
import { Eye, EyeOff } from "lucide-react";
import debug from "./debug";
import "./AppMobile.css";

const LIBRARIES: ["places"] = ["places"];

// Global flag to ensure Google Maps script is only loaded once per page
let googleMapsScriptAdded = false;

interface ValidationResult {
  isValid: boolean;
  message?: string;
}

const validateInputs = (
  fromLocation: google.maps.LatLngLiteral | null,
  toLocation: google.maps.LatLngLiteral | null,
  selectedDate: string,
  selectedTime: string,
): ValidationResult => {
  const missingFields = [];
  if (!fromLocation) missingFields.push("Starting location");
  if (!toLocation) missingFields.push("Destination");
  if (!selectedDate) missingFields.push("Date");
  if (!selectedTime) missingFields.push("Time");

  if (missingFields.length > 0) {
    return { isValid: false, message: `Missing: ${missingFields.join(", ")}` };
  }

  const departureTime = new Date(`${selectedDate}T${selectedTime}`);
  if (departureTime <= new Date()) {
    return { isValid: false, message: "Select a future date and time" };
  }

  return { isValid: true };
};

class MapErrorBoundary extends React.Component<{ children: React.ReactNode }> {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  render() {
    if (this.state.hasError) {
      return <WarningMessage message="Failed to load map. Please try again." />;
    }
    return this.props.children;
  }
}

// License status indicator component
const LicenseStatusIndicator: React.FC = () => {
  const { licenseState, dailyRouteCount, dailyRouteLimit } = useLicense();

  if (licenseState.isLoading) {
    return null;
  }

  if (licenseState.isPro) {
    return (
      <div className="license-status license-status-pro">
        Pro License Active
      </div>
    );
  }

  return (
    <div className="license-status license-status-free">
      Free ({dailyRouteLimit - dailyRouteCount} routes remaining today)
    </div>
  );
};

// Main content component that uses license context
const TransitPlannerMobileContent: React.FC = () => {
  const [state, dispatch] = React.useReducer(
    transitPlannerReducer,
    initialState,
  );
  const {
    fromLocation,
    toLocation,
    selectedDate,
    selectedTime,
    directions,
    groupedRoutes,
    warning,
    currentRouteIndex,
    isLoading,
  } = state;
  const [mapLoaded, setMapLoaded] = useState(false);

  // License context for pro features and route limiting
  const { licenseState, canSearchRoutes, recordRouteSearch } = useLicense();

  const { apiKey, center, zoom } = useMemo(() => getConfigSettings(), []);
  const isDevMode = process.env.NODE_ENV === "development";

  // Coverage area - just show it by default, toggle hides it
  const [showCoverageArea, setShowCoverageArea] = useState(true);
  const regionCorners = window.trpSettings?.regionCorners;

  // Toggle coverage area - simple local toggle
  const handleToggleCoverageArea = useCallback(() => {
    setShowCoverageArea(prev => !prev);
  }, []);

  useEffect(() => {
    debug.log(" useEffect triggered, isDevMode:", isDevMode);
    if (isDevMode) {
      debug.log(" Dev mode: Using LoadScript with apiKey:", apiKey);
      setMapLoaded(true);
    } else {
      debug.log(" Non-dev mode: Initializing script load");
      debug.log(" trpSettings:", window.trpSettings);
      debug.log(" mapsAPIUrlProd:", window.trpSettings?.mapsAPIUrlProd);

      // Helper function to wait for google.maps.Map constructor
      const waitForMapsReady = (source: string) => {
        let attempts = 0;
        const maxAttempts = 50; // 5 seconds max

        const checkReady = () => {
          attempts++;
          if (
            typeof window.google?.maps?.Map === "function" &&
            typeof window.google?.maps?.DirectionsService === "function"
          ) {
            setMapLoaded(true);
          } else if (attempts < maxAttempts) {
            setTimeout(checkReady, 100);
          } else {
            dispatch({
              type: "SET_WARNING",
              payload: "Google Maps API failed to initialize",
            });
          }
        };
        checkReady();
      };

      // Check if Google Maps is already loaded (e.g., by standalone demo's index.php)
      if (window.google?.maps) {
        debug.log(" Google Maps detected, checking if fully initialized");
        // With loading=async, google.maps exists but Map constructor may not be ready
        if (
          typeof window.google.maps.Map === "function" &&
          typeof window.google.maps.DirectionsService === "function"
        ) {
          debug.log(" Google Maps fully ready, skipping script load");
          setMapLoaded(true);
        } else {
          waitForMapsReady("pre-loaded");
        }
        return;
      }

      // Use googleMapsProxyUrl if set (standalone demo), otherwise use WordPress REST API
      const scriptUrl =
        window.trpSettings?.googleMapsProxyUrl ||
        (window.trpSettings?.mapsAPIUrlProd
          ? "/wp-json/trp/v1/proxy-google-maps"
          : "");
      debug.log(" Computed script URL:", scriptUrl);

      if (!scriptUrl) {
        dispatch({
          type: "SET_WARNING",
          payload: "No Google Maps API URL configured",
        });
        return;
      }

      // Check if we've already added the script (global flag)
      if (googleMapsScriptAdded) {
        debug.log(" Google Maps script already added globally, skipping");
        return;
      }

      // Check if script already exists in DOM
      const existingScript = document.querySelector('script[data-trp-google-maps]');
      if (existingScript) {
        debug.log(" Google Maps script already in DOM, skipping");
        googleMapsScriptAdded = true;
        return;
      }

      const script = document.createElement("script");
      script.src = scriptUrl;
      script.async = true;
      script.type = "text/javascript";
      script.setAttribute('data-trp-google-maps', 'true');
      debug.log(" Script element created with src:", script.src);

      script.onload = () => {
        debug.log(" Script loaded successfully");
        debug.log(" google.maps available:", !!window.google?.maps);
        (window as any).__trpMapsLoading = false;

        if (
          typeof window.google?.maps?.Map === "function" &&
          typeof window.google?.maps?.DirectionsService === "function"
        ) {
          debug.log(" google.maps.Map immediately available");
          (window as any).__trpMapsLoaded = true;
          setMapLoaded(true);
        } else {
          debug.log(" Waiting for google.maps.Map to be ready...");
          waitForMapsReady("script-load");
        }
      };

      script.onerror = (error) => {
        debug.error(" Script failed to load:", error);
        dispatch({ type: "SET_WARNING", payload: "Failed to load map script" });
      };

      document.head.appendChild(script);
      googleMapsScriptAdded = true;
      debug.log(" Script appended to head:", script.outerHTML);
      
      // No cleanup function - script persists for the lifetime of the page
    }
  }, [isDevMode, apiKey]);

  const { departureDate, departureTime } = useDynamicDateTime(
    selectedDate,
    selectedTime,
  );
  useEffect(() => {
    if (departureDate !== selectedDate || departureTime !== selectedTime) {
      dispatch({ type: "SET_DATE", payload: departureDate });
      dispatch({ type: "SET_TIME", payload: departureTime });
    }
  }, [departureDate, departureTime, selectedDate, selectedTime]);

  useEffect(() => {
    if (!directions?.routes) return;
    const filtered = filterLatLong(directions);
    if (filtered?.routes) {
      const grouped = groupIdenticalRoutes(filtered.routes);
      dispatch({ type: "SET_FILTERED_DIRECTIONS", payload: filtered });
      dispatch({ type: "SET_GROUPED_ROUTES", payload: grouped });
    }
  }, [directions]);

  const onFromLocationChange = useCallback(
    (location: google.maps.LatLngLiteral | null) =>
      dispatch({ type: "SET_FROM_LOCATION", payload: location }),
    [],
  );
  const onToLocationChange = useCallback(
    (location: google.maps.LatLngLiteral | null) =>
      dispatch({ type: "SET_TO_LOCATION", payload: location }),
    [],
  );
  const onDateChange = useCallback(
    (date: string) => dispatch({ type: "SET_DATE", payload: date }),
    [],
  );
  const onTimeChange = useCallback(
    (time: string) => dispatch({ type: "SET_TIME", payload: time }),
    [],
  );
  const onRouteIndexChange = useCallback(
    (index: number) =>
      dispatch({ type: "SET_CURRENT_ROUTE_INDEX", payload: index }),
    [],
  );

  const handleFindRoutes = useCallback(
    debounce(async () => {
      const validation = validateInputs(
        fromLocation,
        toLocation,
        selectedDate,
        selectedTime,
      );
      if (!validation.isValid) {
        dispatch({ type: "SET_WARNING", payload: validation.message! });
        return;
      }

      // Check license-based route limiting
      const routeCheck = canSearchRoutes();
      if (!routeCheck.allowed) {
        dispatch({
          type: "SET_WARNING",
          payload:
            "Daily route limit reached. Upgrade to Pro for unlimited routes.",
        });
        return;
      }

      dispatch({ type: "SET_LOADING", payload: true });
      dispatch({ type: "RESET_SEARCH" });

      const departureTime = new Date(`${selectedDate}T${selectedTime}`);
      const request: google.maps.DirectionsRequest = {
        origin: fromLocation!,
        destination: toLocation!,
        travelMode: google.maps.TravelMode.TRANSIT,
        transitOptions: { departureTime },
        provideRouteAlternatives: true,
      };

      try {
        const { result, status } = await fetchDirections(request);
        if (status === google.maps.DirectionsStatus.OK && result) {
          dispatch({ type: "SET_DIRECTIONS", payload: result });
          // Record the route search for free tier limiting
          recordRouteSearch();
        } else {
          // Show user-friendly message based on status
          dispatch({
            type: "SET_WARNING",
            payload: getDirectionsErrorMessage(status),
          });
        }
      } catch (error) {
        // Only for unexpected errors (network issues, etc.)
        debug.error(" Unexpected error fetching directions:", error);
        dispatch({
          type: "SET_WARNING",
          payload:
            "Something went wrong. Please check your connection and try again.",
        });
      } finally {
        dispatch({ type: "SET_LOADING", payload: false });
      }
    }, 300),
    [
      fromLocation,
      toLocation,
      selectedDate,
      selectedTime,
      canSearchRoutes,
      recordRouteSearch,
    ],
  );

  const isDisabled =
    isLoading ||
    licenseState.isLoading ||
    !validateInputs(fromLocation, toLocation, selectedDate, selectedTime)
      .isValid;

  // Show configuration error or loading state
  if (!mapLoaded) {
    if (warning) {
      return (
        <div className="trp-mobile-container">
          <WarningMessage message={warning} />
        </div>
      );
    }
    return <div className="trp-mobile-container">Loading map...</div>;
  }

  const layoutContent = (
    <>
      <LicenseStatusIndicator />
      <WarningMessage message={warning} />
      <InputGroupMobile
        apiKey={isDevMode ? apiKey : ""}
        onFromLocationChange={onFromLocationChange}
        onToLocationChange={onToLocationChange}
        selectedDate={selectedDate}
        onDateChange={onDateChange}
        selectedTime={selectedTime}
        onTimeChange={onTimeChange}
      />
      <FindRoutesButton
        onClick={() => Promise.resolve(handleFindRoutes())}
        disabled={isDisabled}
        isLoading={isLoading}
      />
      {/* Coverage Area Toggle - compact */}
      <button
        type="button"
        className="coverage-toggle-button"
        onClick={handleToggleCoverageArea}
      >
        {showCoverageArea ? <EyeOff size={16} /> : <Eye size={16} />}
        <span>{showCoverageArea ? "Hide Coverage" : "Show Coverage"}</span>
      </button>
    </>
  );

  return (
    <MapErrorBoundary>
      {isDevMode ? (
        <LoadScript
          googleMapsApiKey={apiKey}
          libraries={LIBRARIES}
          version="weekly"
        >
          <LayoutMobile
            map={
              <MapContainer
                directions={directions}
                currentRouteIndex={currentRouteIndex}
                center={center}
                zoom={zoom}
                showRectangularCoverageArea={showCoverageArea}
                regionCorners={regionCorners}
              />
            }
            routes={
              <RouteCard
                groupedRoutes={groupedRoutes}
                currentRouteIndex={currentRouteIndex}
                onRouteIndexChange={onRouteIndexChange}
              />
            }
          >
            {layoutContent}
          </LayoutMobile>
        </LoadScript>
      ) : (
        <LayoutMobile
          map={
            <MapContainer
              directions={directions}
              currentRouteIndex={currentRouteIndex}
              center={center}
              zoom={zoom}
              showRectangularCoverageArea={showCoverageArea}
              regionCorners={regionCorners}
            />
          }
          routes={
            <RouteCard
              groupedRoutes={groupedRoutes}
              currentRouteIndex={currentRouteIndex}
              onRouteIndexChange={onRouteIndexChange}
            />
          }
        >
          {layoutContent}
        </LayoutMobile>
      )}
    </MapErrorBoundary>
  );
};

// Root App component wrapped with LicenseProvider
const AppMobile: React.FC = () => {
  return (
    <LicenseProvider>
      <TransitPlannerMobileContent />
    </LicenseProvider>
  );
};

export default AppMobile;
